import { Component, OnInit, AfterViewInit, OnDestroy, ViewChild, ElementRef, Renderer2 } from "@angular/core";
import { DomSanitizer, SafeResourceUrl } from "@angular/platform-browser";
import { NGXLogger } from "ngx-logger";
import { DynamicDialogConfig, DynamicDialogRef } from "primeng/dynamicdialog";

@Component({
  selector: "app-progetta-pratica",
  templateUrl: "./dialog-progetta-pratica.component.html",
  styleUrls: ["./progetta-pratica.component.scss"],
})
export class DialogProgettaPraticaComponent implements OnInit, AfterViewInit, OnDestroy {
  public urlViewer: SafeResourceUrl;
  public rawUrl: string; // URL originale non sanitizzato per aprire in nuova finestra
  public isLoading: boolean = true;
  @ViewChild('iframeElement') iframeElement: ElementRef<HTMLIFrameElement>;
  private checkInterval: any;
  private loadTimeout: any;
  private loadEventCount: number = 0; // Contatore per tracciare quanti eventi load sono stati ricevuti

  constructor(
    private logger: NGXLogger,
    public dialogRef: DynamicDialogRef,
    public config: DynamicDialogConfig,
    private sanitizer: DomSanitizer,
    private renderer: Renderer2,
  ) {}

  ngOnInit(): void {
    if (this.config.data && this.config.data.urlViewer) {
      // Salva l'URL originale per aprire in nuova finestra
      this.rawUrl = this.config.data.urlViewer;
      // Sanitizza l'URL per permettere il caricamento nell'iframe
      this.urlViewer = this.sanitizer.bypassSecurityTrustResourceUrl(this.config.data.urlViewer);
      this.logger.info("URL Viewer caricato:", this.config.data.urlViewer);
    }
  }

  ngAfterViewInit(): void {
    // Aggiungi pulsante per aprire in nuova finestra nell'header del dialog
    // Usa un delay maggiore per assicurarsi che il dialog sia completamente renderizzato
    const addButton = () => {
      // Prova a trovare il dialog con diversi selettori
      let dialogElement = document.querySelector('.viewer-dialog .p-dialog') as HTMLElement;
      if (!dialogElement) {
        // Prova senza la classe specifica
        dialogElement = document.querySelector('p-dynamicdialog .p-dialog') as HTMLElement;
      }
      
      if (!dialogElement) {
        // Prova un selettore ancora più generico
        const allDialogs = document.querySelectorAll('.p-dialog');
        dialogElement = Array.from(allDialogs).find(d => {
          const header = d.querySelector('.p-dialog-header .p-dialog-title');
          return header && header.textContent && header.textContent.includes('Progettazione');
        }) as HTMLElement;
      }

      if (dialogElement) {
        // Aggiungi pulsante per aprire in nuova finestra nell'header
        const headerElement = dialogElement.querySelector('.p-dialog-header');
        if (headerElement) {
          // Verifica se il pulsante esiste già per evitare duplicati
          let openInNewWindowBtn = headerElement.querySelector('.open-in-new-window-btn') as HTMLElement;
          
          if (!openInNewWindowBtn) {
            // Trova il container delle icone dell'header
            let headerIcons = headerElement.querySelector('.p-dialog-header-icons') as HTMLElement;
            
            if (!headerIcons) {
              // Se non esiste, crealo
              headerIcons = this.renderer.createElement('div');
              this.renderer.addClass(headerIcons, 'p-dialog-header-icons');
              this.renderer.appendChild(headerElement, headerIcons);
            }

            // Crea un wrapper span per il tooltip di PrimeNG
            const tooltipWrapper = this.renderer.createElement('span');
            this.renderer.setAttribute(tooltipWrapper, 'pTooltip', 'Apri in nuova finestra');
            this.renderer.setAttribute(tooltipWrapper, 'tooltipPosition', 'bottom');
            this.renderer.setAttribute(tooltipWrapper, 'showDelay', '300');
            this.renderer.setStyle(tooltipWrapper, 'display', 'inline-flex');
            
            // Crea il pulsante
            openInNewWindowBtn = this.renderer.createElement('button');
            this.renderer.addClass(openInNewWindowBtn, 'open-in-new-window-btn');
            this.renderer.addClass(openInNewWindowBtn, 'p-dialog-header-icon');
            this.renderer.addClass(openInNewWindowBtn, 'p-link');
            this.renderer.setAttribute(openInNewWindowBtn, 'type', 'button');
            this.renderer.setAttribute(openInNewWindowBtn, 'aria-label', 'Apri in nuova finestra');
            this.renderer.setAttribute(openInNewWindowBtn, 'role', 'button');
            
            // Crea lo span per l'icona
            const iconSpan = this.renderer.createElement('span');
            this.renderer.addClass(iconSpan, 'pi');
            this.renderer.addClass(iconSpan, 'pi-external-link');
            this.renderer.appendChild(openInNewWindowBtn, iconSpan);
            
            // Aggiungi il pulsante al wrapper
            this.renderer.appendChild(tooltipWrapper, openInNewWindowBtn);
            
            // Aggiungi l'event listener
            this.renderer.listen(openInNewWindowBtn, 'click', (e: Event) => {
              e.preventDefault();
              e.stopPropagation();
              this.apriInNuovaFinestra();
            });

            // Inserisci il wrapper (con il pulsante dentro) nell'header icons
            // Cerca il pulsante di massimizzazione per inserirlo prima di quello
            const maximizeButton = headerIcons.querySelector('.p-dialog-header-icon.p-dialog-header-maximize') as HTMLElement;
            if (maximizeButton) {
              this.renderer.insertBefore(headerIcons, tooltipWrapper, maximizeButton);
            } else {
              // Se non c'è il pulsante di massimizzazione, aggiungi alla fine
              this.renderer.appendChild(headerIcons, tooltipWrapper);
            }
            
            this.logger.info("Pulsante 'Apri in nuova finestra' aggiunto all'header con tooltip PrimeNG");
          }
        }
      } else {
        // Se non trovato, riprova dopo un po'
        setTimeout(addButton, 200);
      }
    };

    // Prova subito e poi con un delay
    setTimeout(addButton, 100);
    setTimeout(addButton, 300);
    setTimeout(addButton, 500);
  }

  onIframeLoad(): void {
    this.loadEventCount++;
    this.logger.info(`Iframe load event emesso (${this.loadEventCount}° volta)`);
    
    // Il load event viene emesso più volte:
    // 1. La prima volta quando viene caricato l'HTML iniziale (troppo presto - da ignorare)
    // 2. La seconda volta quando è effettivamente caricato tutto il contenuto (questo è quello giusto)
    
    // Se è il primo evento, ignoriamolo
    if (this.loadEventCount === 1) {
      this.logger.info("Primo evento load ignorato - attendo il secondo evento");
      return;
    }
    
    // Se è il secondo evento o successivo, nascondi lo spinner immediatamente
    if (this.loadEventCount >= 2) {
      this.logger.info(`Secondo evento load ricevuto (${this.loadEventCount}°) - contenuto caricato, nascondo spinner`);
      
      // Cancella eventuali timeout precedenti
      if (this.loadTimeout) {
        clearTimeout(this.loadTimeout);
      }
      if (this.checkInterval) {
        clearInterval(this.checkInterval);
      }
      
      // Nascondi lo spinner immediatamente
      this.hideSpinner();
      this.cleanup();
      return;
    }
  }
  
  private cleanup(): void {
    if (this.checkInterval) {
      clearInterval(this.checkInterval);
      this.checkInterval = null;
    }
    if (this.loadTimeout) {
      clearTimeout(this.loadTimeout);
      this.loadTimeout = null;
    }
  }

  private hideSpinner(): void {
    if (this.isLoading) {
      this.isLoading = false;
      this.logger.info("Spinner nascosto - contenuto remoto caricato");
    }
  }

  ngOnDestroy(): void {
    // Pulisci gli interval e timeout quando il componente viene distrutto
    this.cleanup();
  }

  apriInNuovaFinestra(): void {
    if (this.rawUrl) {
      this.logger.info("Apertura URL in nuova finestra:", this.rawUrl);
      window.open(this.rawUrl, '_blank', 'noopener,noreferrer');
      // Chiudi il dialog
      this.dialogRef.close();
    }
  }

  chiudi() {
    this.dialogRef.close();
  }
}
